/**
 * useSiteStatusPdfManifest — fetch + cache the Site Status PDF picker
 * field manifest from /api/site_status_pdf_field_manifest.
 *
 * Mirrors the loading / error / data shape of useDataExchangeManifest with
 * one addition: a 5-minute module-scoped in-memory cache. The manifest is
 * small (~6kB) and changes only as part of feature ships, so refetching on
 * every modal open is wasteful. Module-scoped means the cache survives
 * component unmount/remount within the tab session but is fresh on tab
 * reload.
 *
 * Returns: { manifest, loading, error, refetch }.
 *
 * No 400 paths to handle — the manifest endpoint is GET-only and either
 * succeeds or fails at the network layer. Error path returns a string
 * message; manifest stays null. The picker renders an error state with
 * a Retry button that calls refetch().
 */

import { useEffect, useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
const TTL_MS = 5 * 60 * 1000;

// Module-scoped cache. Survives component remounts within a tab session.
let cachedManifest = null;
let cachedAt = 0;

export function useSiteStatusPdfManifest() {
  const [manifest, setManifest] = useState(() => {
    if (cachedManifest && Date.now() - cachedAt < TTL_MS) return cachedManifest;
    return null;
  });
  const [loading, setLoading] = useState(() => {
    return !(cachedManifest && Date.now() - cachedAt < TTL_MS);
  });
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);  // bumping forces a refetch

  useEffect(() => {
    // Cache hit on initial mount: state already set from cache, skip fetch.
    if (cachedManifest && Date.now() - cachedAt < TTL_MS && version === 0) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${API_BASE_URL}/site_status_pdf_field_manifest?report=site_status`)
      .then(async (res) => {
        if (!res.ok) {
          let msg = `Manifest fetch failed (HTTP ${res.status})`;
          try {
            const body = await res.json();
            if (body?.error) msg = body.error;
          } catch (_) { /* not JSON; keep generic */ }
          throw new Error(msg);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        cachedManifest = data;
        cachedAt = Date.now();
        setManifest(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('useSiteStatusPdfManifest:', err);
        setManifest(null);
        setError(err.message || 'Could not load field manifest.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [version]);

  const refetch = useCallback(() => {
    cachedManifest = null;
    cachedAt = 0;
    setVersion((v) => v + 1);
  }, []);

  return { manifest, loading, error, refetch };
}
